import type { AcceptInvitationRequest, DataResponse, Invitation, InvitationRequest, PublicInvitation } from '@/types';
import type { AxiosInstance, AxiosResponse } from 'axios';

/**
 * Seat invitations (MILO-178).
 *
 * Two tiers with different bases: the account-scoped management endpoints live
 * under `/api` behind the normal auth, while the invitee's two endpoints are
 * public routes in milo-api's `routes/web.php` — they have to be, since whoever
 * opens the mailed link has no login yet.
 */
class InvitationService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string) {
        return id ? `/api/invitations/${id}` : '/api/invitations';
    }

    /** Every invitation on your account (pending, accepted and expired), newest first. */
    index(): Promise<AxiosResponse<DataResponse<Invitation[]>>> {
        return this.apiService.get(this.getPath());
    }

    /** Re-inviting the same address rotates the token on the existing row. */
    store(request: InvitationRequest): Promise<AxiosResponse<DataResponse<Invitation>>> {
        return this.apiService.post(this.getPath(), request);
    }

    /**
     * Mail an open invitation again, rotating its token. Separate from `store`
     * because inviting an address that already has an open invitation is refused
     * — re-sending has to be deliberate, and carries a server-side cooldown
     * (429) so it can't be used to spam someone's inbox. useInvitations names
     * that cooldown itself, so the global rate-limit notice stays out of it.
     */
    resend(id: string): Promise<AxiosResponse<DataResponse<Invitation>>> {
        return this.apiService.post(`${this.getPath(id)}/resend`, undefined, { skipRateLimitNotice: true });
    }

    destroy(id: string): Promise<AxiosResponse<void>> {
        return this.apiService.delete(this.getPath(id));
    }

    /** Public: who invited the visitor, for the accept page. 404 once expired or accepted. */
    showPublic(token: string): Promise<AxiosResponse<DataResponse<PublicInvitation>>> {
        return this.apiService.get(`/invitations/${token}`, { skipPlanGate: true });
    }

    /**
     * Public: turn the invitation into a real user. Needs a CSRF cookie like the
     * other web routes (register / reset-password).
     *
     * `skipPlanGate` keeps the global upgrade modal out of this: the 403 here
     * means the *inviter's* account shrank while the link sat in an inbox, and
     * the invitee — who has no account and no billing page — can't act on an
     * upsell. The page renders that case itself.
     */
    async accept(token: string, request: AcceptInvitationRequest): Promise<AxiosResponse<{ message: string }>> {
        await this.apiService.auth.csrf();
        return this.apiService.post(`/invitations/${token}`, request, { skipPlanGate: true });
    }
}

export default InvitationService;
